import axios from 'axios';
import { InteractionRequiredAuthError } from '@azure/msal-browser';
import { msalInstance } from '../auth/msalInstance';
import { apiRequest } from '../auth/authConfig';

// Marks that we've already bounced the user through an interactive sign-in this
// session. It survives the redirect navigation (sessionStorage persists across
// same-tab loads), so on return we DON'T immediately bounce again — the first
// healthy call clears it. Without this guard, a session that re-auth can't fix
// (a genuine config problem) would redirect-loop forever; better to surface the
// error than trap the user in a bounce.
const REAUTH_FLAG = 'dashboardReauthing';

// The signed-in account. MSAL can hold a cached sign-in with NO active account
// (several tabs, a second Microsoft account, a half-finished sign-out). Every
// request then went out with no token ("Missing authentication token" on the
// Helpdesk, 01/10/2026) and nothing recovered, because re-sign-in also bailed
// out without an active account. Use the cached account and make it active;
// null only when nobody is signed in at all.
function currentAccount() {
  let account = msalInstance.getActiveAccount();
  if (!account) {
    const all = msalInstance.getAllAccounts();
    if (all.length) {
      [account] = all;
      msalInstance.setActiveAccount(account);
    }
  }
  return account;
}

// Send the user through an interactive redirect to mint a fresh token — once.
// acquireTokenRedirect navigates the whole page away, so any in-flight request
// that triggered this is abandoned; that's fine, the app reloads authenticated.
async function reauthenticate() {
  if (sessionStorage.getItem(REAUTH_FLAG)) return;
  const account = currentAccount();
  sessionStorage.setItem(REAUTH_FLAG, '1');
  // With no cached sign-in at all, a full sign-in is the only way back.
  if (account) await msalInstance.acquireTokenRedirect({ ...apiRequest, account });
  else await msalInstance.loginRedirect({ ...apiRequest });
}

const api = axios.create({
  baseURL: process.env.REACT_APP_API_BASE_URL,
});

api.interceptors.request.use(async (config) => {
  const account = currentAccount();
  if (!account) return config;
  try {
    const { accessToken } = await msalInstance.acquireTokenSilent({
      ...apiRequest,
      account,
    });
    sessionStorage.removeItem(REAUTH_FLAG); // silent worked — session is healthy
    config.headers.Authorization = `Bearer ${accessToken}`;
  } catch (err) {
    // A stale/expired session (common on mobile, where the token lapses between
    // visits) makes silent acquisition fail. Rather than fire the request with
    // no token and leave the user staring at an empty sidebar, re-authenticate
    // interactively to refresh it. Only InteractionRequiredAuthError is
    // recoverable this way — a transient network error (offline) must NOT
    // trigger a sign-in redirect, so we rethrow those untouched.
    if (err instanceof InteractionRequiredAuthError) {
      try {
        await reauthenticate();
      } catch (e) {
        /* redirect couldn't start — fall through and reject the request */
      }
    }
    throw err;
  }
  return config;
});

api.interceptors.response.use(
  (response) => {
    sessionStorage.removeItem(REAUTH_FLAG); // a 2xx means the token is good
    return response;
  },
  async (error) => {
    // 401: the server rejected the token even though silent acquisition handed
    // one over (it expired server-side, or the cached token is for a stale
    // session). One interactive refresh normally fixes it; the guard in
    // reauthenticate() stops it looping if it doesn't.
    if (error.response?.status === 401) {
      try {
        await reauthenticate();
      } catch (e) {
        /* fall through and reject */
      }
    }
    return Promise.reject(error);
  }
);

export default api;
